import { Checkbox } from '../../atoms/Checkbox';
import { Text } from '../../atoms/Typography/Text';
import { useTranslation } from '../../../i18n/useTranslation';
import { situationSteps, type EmergencySituationKey } from '../../../content/emergency/situations';
import './EmergencyChecklist.css';

export interface EmergencyChecklistProps {
  situation: EmergencySituationKey;
  /** Ticked steps → when they were ticked (ISO). */
  ticked: Readonly<Record<string, string>>;
  onToggle: (step: string, checked: boolean) => void;
  disabled?: boolean;
}

/**
 * The first-aid steps of one emergency situation as an ordered checklist
 * (wireframe "Emergency Mode"). Fully bundled — it renders without network.
 * The tick time is shown next to a ticked step (a timeline of what was done).
 */
export function EmergencyChecklist({ situation, ticked, onToggle, disabled = false }: EmergencyChecklistProps) {
  const { t, i18n } = useTranslation();
  const time = (iso: string): string => new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  return (
    <ol className="emergency-checklist" data-testid="emergency-checklist">
      {situationSteps(situation).map((step) => {
        const at = ticked[step];
        return (
          <li key={step} className="emergency-checklist__item">
            <Checkbox
              label={String(t(`emergency.situations.${situation}.steps.${step}`))}
              checked={at !== undefined}
              disabled={disabled}
              onChange={(e) => onToggle(step, e.target.checked)}
            />
            {at !== undefined && (
              <Text as="span" variant="caption" color="muted" className="emergency-checklist__time">
                {t('emergency.tickedAt', { time: time(at) })}
              </Text>
            )}
          </li>
        );
      })}
    </ol>
  );
}
